import type { CSSProperties } from 'react';

import { getFocusable } from '@/components/layout/focus-trap';

import type { SelectOption } from './Select';

// Lógica pura del Select (sin React): nombre desde la etiqueta del campo, posición de la
// lista respecto de la ventana y búsqueda por letras.

/** Texto de la etiqueta que envuelve o apunta al disparador, sin el valor que muestra. */
export function fieldLabel(trigger: HTMLButtonElement | null): string | undefined {
  const label = trigger?.labels?.[0];
  if (label === undefined || trigger === null) {
    return undefined;
  }
  const text = Array.from(label.childNodes)
    .filter((n) => !n.contains(trigger))
    .map((n) => n.textContent ?? '')
    .join(' ')
    .trim();
  return text === '' ? undefined : text;
}

const GAP = 6;
const ROOM = 240;

/**
 * Posición fija de la lista bajo el disparador (o sobre él si no hay lugar debajo),
 * alineada al borde del disparador más cercano al centro, para no salirse de la pantalla.
 */
export function listPosition(
  rect: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right' | 'width'>,
  view: Pick<Window, 'innerWidth' | 'innerHeight'>,
): CSSProperties {
  const below = view.innerHeight - rect.bottom;
  const up = below < ROOM && rect.top > below;
  const vertical = up
    ? { bottom: view.innerHeight - rect.top + GAP, maxHeight: Math.min(280, rect.top - 2 * GAP) }
    : { top: rect.bottom + GAP, maxHeight: Math.min(280, below - 2 * GAP) };
  const rightHalf = rect.left + rect.width / 2 > view.innerWidth / 2;
  const horizontal = rightHalf ? { right: view.innerWidth - rect.right } : { left: rect.left };
  return { position: 'fixed', minWidth: rect.width, ...vertical, ...horizontal };
}

/**
 * Siguiente opción habilitada cuyo texto empieza con lo escrito. Con una sola letra
 * repetida recorre las coincidencias desde la activa; con varias, busca el prefijo.
 */
export function nextMatch(
  options: readonly SelectOption[],
  enabled: readonly number[],
  active: number,
  typed: string,
): number | undefined {
  const text = typed.toLocaleLowerCase('es');
  const repeated = text.length > 1 && [...text].every((c) => c === text[0]);
  const prefix = repeated ? (text[0] ?? '') : text;
  const matches = enabled.filter((i) =>
    options[i]?.label.toLocaleLowerCase('es').startsWith(prefix),
  );
  if (matches.length === 0) {
    return undefined;
  }
  if (prefix.length > 1) {
    return matches[0];
  }
  return matches.find((i) => i > active) ?? matches[0];
}

/**
 * Tab desde la lista abierta: el foco pasa al control siguiente al disparador (dentro del
 * diálogo que lo contiene, si lo hay), como si se hubiera tabulado desde el disparador.
 * Sin control siguiente, queda en el disparador.
 */
export function focusAfter(trigger: HTMLElement): void {
  const scope = trigger.closest<HTMLElement>('[role="dialog"]') ?? document.body;
  const items = getFocusable(scope);
  const next = items[items.indexOf(trigger) + 1];
  (next ?? trigger).focus();
}
